<template>
  <div class="vehicle-finder-form critical-component-clear">
    <tabs-snippet
      ref="tabs"
      class="vehicle-finder-form__tabs-snippet"
      :classes="{
        tab: 'vehicle-finder-form__tab',
        tabLabel: 'text-body-xs-bold text-heading-m-desktop',
        tabslist: 'vehicle-finder-form__tabs',
        tabslistContainer: 'vehicle-finder-form__tabs-container',
      }"
      :content="false"
      :heading-id="namespace"
      :namespace="namespace"
      :parent-active-tab="activeTab"
      :tabs="tabs"
      @selected-tab="handleTabUpdate"
    />

    <div
      ref="panelContainer"
      class="vehicle-finder-form__panel-container"
      :class="{ 'vehicle-finder-form__panel-container--overflow': widths.tabs >= widths.panel }"
      :style="{
        height: panelContainerHeight,
        overflow: panelOverflow,
      }"
    >
      <template
        v-for="(tab, index) of tabs"
        :key="`${tab.id}-${index}`"
      >
        <transition
          v-if="tab.id === 'dropdowns'"
          name="fade"
          @enter="handleEnter('tabs')"
          @after-leave="handleAfterLeave"
        >
          <div
            v-show="
              activeTab?.id === tab.id ||
                !activeTab?.id && tabs[0].id === tab.id
            "
            :id="`panel-${index}-${namespace}`"
            role="tabpanel"
            :aria-labelledby="`tab-${index}-${namespace}`"
            :data-id="tab.id"
          >
            <transition
              name="fade"
              @enter="handleEnter('content')"
              @after-leave="handleAfterLeave"
            >
              <form-dropdowns
                v-show="results !== 0"
                :ref="tab.id"
                class="vehicle-finder-form__panel"
                :forwarding="forwarding"
                :is-active="activeTab?.id === tab.id"
                :namespace="namespace"
                :overlay="overlay"
                :results="results"
                :template="template"
                data-id="dropdowns"
                @existing-vehicle="handleExistingVehicle"
                @vehicle-response="handleVehicleResponse"
              />
            </transition>

            <transition
              name="fade"
              @enter="handleEnter('content')"
              @after-leave="handleAfterLeave"
            >
              <form-missing
                v-show="results === 0"
                :ref="`${tab.id}Missing`"
                class="vehicle-finder-form__panel"
                :data-id="tab.id"
                :form-id="`dropdowns-${namespace}`"
                :vehicle="missingManualVehicle"
                @search-again="handleSearchAgain"
              />
            </transition>
          </div>
        </transition>

        <transition
          v-else-if="tab.id === 'vrm'"
          name="fade"
          @enter="handleEnter('tabs')"
          @after-leave="handleAfterLeave"
        >
          <div
            v-show="
              activeTab?.id === tab.id ||
                !activeTab?.id && tabs[0].id === tab.id
            "
            :id="`panel-${index}-${namespace}`"
            role="tabpanel"
            :aria-labelledby="`tab-${index}-${namespace}`"
            :data-id="tab.id"
          >
            <transition
              name="fade"
              @enter="handleEnter('content')"
              @after-leave="handleAfterLeave"
            >
              <form-vrm
                v-show="results !== 0"
                :ref="tab.id"
                class="vehicle-finder-form__panel"
                :forwarding="forwarding"
                :is-active="activeTab?.id === tab.id"
                :namespace="namespace"
                :overlay="overlay"
                :results="results"
                :data-id="tab.id"
                @existing-vehicle="handleExistingVehicle"
                @vehicle-response="handleVehicleResponse"
              />
            </transition>

            <transition
              name="fade"
              @enter="handleEnter('content')"
              @after-leave="handleAfterLeave"
            >
              <form-missing
                v-show="results === 0"
                :ref="`${tab.id}Missing`"
                class="vehicle-finder-form__panel"
                :data-id="tab.id"
                :form-id="`vrm-${namespace}`"
                :vehicle="missingManualVehicle"
                @search-again="handleSearchAgain"
              />
            </transition>
          </div>
        </transition>

        <transition
          v-else-if="tab.id === 'vin'"
          name="fade"
          @enter="handleEnter('tabs')"
          @after-leave="handleAfterLeave"
        >
          <div
            v-show="
              activeTab?.id === tab.id ||
                !activeTab?.id && tabs[0].id === tab.id
            "
            :id="`panel-${index}-${namespace}`"
            role="tabpanel"
            :aria-labelledby="`tab-${index}-${namespace}`"
            :data-id="tab.id"
          >
            <transition
              name="fade"
              @enter="handleEnter('content')"
              @after-leave="handleAfterLeave"
            >
              <form-vin
                v-show="results !== 0"
                :ref="tab.id"
                class="vehicle-finder-form__panel"
                :forwarding="forwarding"
                :is-active="activeTab?.id === tab.id"
                :namespace="namespace"
                :overlay="overlay"
                :results="results"
                :data-id="tab.id"
                @existing-vehicle="handleExistingVehicle"
                @vehicle-response="handleVehicleResponse"
              />
            </transition>

            <transition
              name="fade"
              @enter="handleEnter('content')"
              @after-leave="handleAfterLeave"
            >
              <form-missing
                v-show="results === 0"
                :ref="`${tab.id}Missing`"
                class="vehicle-finder-form__panel"
                :data-id="tab.id"
                :form-id="`vin-${namespace}`"
                :vehicle="missingManualVehicle"
                @search-again="handleSearchAgain"
              />
            </transition>
          </div>
        </transition>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Vehicle finder form (vehicle-finder-form)
 * -----------------------------------------------------------------------------
 * Form to run VIN, VRM, and Make/Model/Year searches to find user vehicle.
 *
 * @param {String} namespace - Unique namespace.
 * @param {Object} overlay - Overlay API.
 * @param {String} template - Template that contains form, `modal` or `section`.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import FormDropdowns from './form-dropdowns'
import FormMissing from './form-missing'
import FormVrm from './form-vrm'
import FormVin from './form-vin'
import TabsSnippet from '~async/tabs-snippet/tabs-snippet'

import vehicleFinderQuery from '~graphql/vehicle-finder.gql'

import { focusable } from '~/helpers/accessibility'
import { buildEventParameters, convertVrmIdMetafieldToVehicle } from '~/helpers/garage'
import { transformEdges } from '~/helpers/graphql'

export default {
  name: 'VehicleFinderForm',

  components: {
    FormDropdowns,
    FormMissing,
    FormVin,
    FormVrm,
    TabsSnippet,
  },

  props: {
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    template: {
      type: String,
      default: 'modal',
      required: true,
    },
  },

  data() {
    return {
      activeTab: {
        id: '',
        title: '',
      },
      animationType: 'tabs',
      elements: {
        activeTab: false,
        previousTab: false,
      },
      forwarding: false,
      missingManualVehicle: false,
      panelContainerHeight: 'auto',
      panelOverflow: null,
      results: false,
      tabs: [],
      widths: {
        panel: 0,
        tabs: 0,
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFormattedVehicle: 'garage/getFormattedVehicle',
    }),
  },

  watch: {

    /**
     * Watch active tab to update panel container height for transition.
     * - Also update overlay focus elements.
     * @param {Object} newValue - New active tab.
     * @param {Object} oldValue - Old active tab.
     */
    activeTab: {
      handler(newValue, oldValue) {
        if (!oldValue?.id) {
          return
        }

        this.forwarding = false
        this.results = false

        this.animationType = 'tabs'
        this.elements.activeTab = this.$refs[newValue.id][0]?.$el
        this.elements.previousTab = this.$refs[oldValue.id][0]?.$el

        this.updateFocus(newValue?.id)
      },
      immediate: true,
    },

    /**
     * Watch overlay isActive state to reset form to first tab.
     */
    'overlay.isActive'() {
      if (!this.overlay.isActive) {
        return
      }

      this.activeTab = this.tabs[0]
    },
  },

  /**
   * Set data state based on theme setting before starting component.
   */
  created() {
    this.tabs = this.$variable('vehicleFinder.tabsOrder').map((id) => {
      return {
        id,
        title: this.$string(`vehicle_finder.tabs.${id}`),
      }
    })
  },

  async mounted() {
    this.setWidths()
    cnvs.EventBus.listen('cnvs:resize', this.setWidths)

    if (this.template !== 'modal') {
      return
    }

    await this.$nextTick()
    this.overlay.update()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addVehicle: 'garage/add',
      setActiveVehicle: 'garage/setActive',
    }),

    /**
     * Update focus after transition animation has completed.
     * - Only listen for transition on matching element.
     * @param {String} id - ID to check against.
     * @param {HTMLElement} container - Container to look for focusable elements
     * inside of instead of using active element.
     */
    updateFocus(id, container) {
      this.$refs.panelContainer.ontransitionend = async(args) => {
        if (!args || args.srcElement?.dataset?.id !== id) {
          return
        }

        await cnvs.Sleep(0)
        const focusableElements = container ? focusable(container) : []

        const currentElement = focusableElements.length
          ? focusableElements[0]
          : document.activeElement

        if (this.template === 'modal') {
          this.overlay.update({ currentElement })
          return
        }

        cnvs.Focus(currentElement)
      }
    },

    /**
     * Set widths.
     */
    setWidths() {
      this.widths.tabs = this?.$refs?.tabs?.$refs?.tabslist?.offsetWidth
      this.widths.panel = this?.$refs?.panelContainer?.offsetWidth
    },

    /**
     * Handle active tab update.
     * @param {String} tab - Active tab.
     */
    handleTabUpdate(tab) {
      this.activeTab = tab
    },

    /**
     * Handle enter transition.
     * @param {String} type - Animation type.
     */
    handleEnter(type) {
      if (type !== this.animationType) {
        return
      }

      const previousHeight = `${this.elements.previousTab?.offsetHeight}px`
      const newHeight = `${this.elements.activeTab?.offsetHeight}px`

      if (previousHeight === newHeight) {
        return
      }

      this.panelOverflow = 'hidden'
      this.panelContainerHeight = previousHeight

      requestAnimationFrame(() => {
        this.panelContainerHeight = newHeight
      })
    },

    /**
     * Handle after leave transition.
     */
    handleAfterLeave() {
      this.panelContainerHeight = 'auto'
      this.panelOverflow = null
    },

    /**
     * Handle search again.
     * - Sets results to false so the original form is shown.
     * - Also clears missing manual vehicle.
     */
    handleSearchAgain() {
      this.results = false
      this.missingManualVehicle = false
      this.triggerAnimation(false)
    },

    /**
     * Handle existing vehicle response from form.
     * - If vehicle exists in garage then results check has already been
     *   performed so vehicle can be immediately made active.
     * @param {import("~stores/garage").Vehicle} vehicle - Vehicle.
     */
    handleExistingVehicle(vehicle) {
      const formattedVehicle = this.getFormattedVehicle(vehicle)
      const eventParameters = buildEventParameters(formattedVehicle)
      this.setActiveVehicle(vehicle.id)
      this.emitEvent('vehicle_search', eventParameters)

      if (this.template === 'section') {
        this.forwarding = true
        location.href = formattedVehicle.resultsUrl
        return
      }

      this.overlay.close()
    },

    /**
     * Handle vehicle response from form.
     * @param {import("~stores/garage").Vehicle} formVehicle - Vehicle, if it's
     * come from VRM or VIN forms then it won't be complete.
     */
    async handleVehicleResponse(formVehicle) {
      let vehicle = formVehicle
      let formattedVehicle = this.getFormattedVehicle(vehicle)

      const includeVrmIdMetafield = ['vrm', 'vin'].includes(formVehicle.method)
      const response = await this.queryResultsCollection(formattedVehicle, includeVrmIdMetafield)
      let results = response.length

      /**
       * If VRM or VIN then use response data to populate vehicle object.
       */
      if (results && includeVrmIdMetafield) {
        const product = response[0]
        const vrmIdVehicle = convertVrmIdMetafieldToVehicle({ product, vehicle })

        if (vrmIdVehicle) {
          vehicle = vrmIdVehicle
          formattedVehicle = this.getFormattedVehicle(vehicle)
        } else {
          results = 0
        }
      }

      /**
       * Build event parameters for event emit.
       * - If VRM or VIN form has no results then parameters will be empty.
       */
      const eventParameters = buildEventParameters(formattedVehicle)

      /**
       * Handle missing manual.
       * - If VRM or VIN the absence of products means we can't populate vehicle
       *   data from products, set vehicle to false to hide garage hero banner
       *   from missing form.
       */
      if (results === 0) {
        this.results = results

        this.missingManualVehicle = includeVrmIdMetafield
          ? false
          : formattedVehicle

        this.triggerAnimation(true)

        if (!includeVrmIdMetafield) {
          this.emitEvent('vehicle_search_non_saleable', eventParameters)
        }

        return
      }

      /**
       * Add vehicle to garage.
       */
      this.addVehicle({ setActive: true, vehicle })
      this.emitEvent('vehicle_search', eventParameters)

      if (this.template === 'section') {
        this.forwarding = true
        location.href = formattedVehicle.resultsUrl
        return
      }

      this.overlay.close()
    },

    /**
     * Query results collection for number of results.
     * - Only need to return one product to know there are results.
     * - For VRM ID searches there will only ever be one result.
     * @param {import("~stores/garage").FormattedVehicle} formattedVehicle
     * @param {Boolean} [includeVrmIdMetafield] - Include VRM ID metafield data
     * in response.
     * @returns {Promise<Number>}
     */
    queryResultsCollection(formattedVehicle, includeVrmIdMetafield = false) {
      return new Promise(async(resolve, reject) => {
        try {
          const metafields = includeVrmIdMetafield
            ? [{ namespace: 'filter', key: 'vehicles' }]
            : []

          const response = await cnvs.Query({
            query: vehicleFinderQuery,
            variables: {
              country: this.$variable('store.country'),
              handle: this.$variable('vehicleFinder.resultsCollection'),
              includeFilters: false,
              language: this.$variable('store.language'),
              metafields,
              productCount: 1,
              productFilters: formattedVehicle.filters,
            },
          })

          if (!response?.collection?.products) {
            resolve([])
            return
          }

          const products = transformEdges(response.collection.products)
          resolve(products)

        } catch (error) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject(0)
        }
      })
    },

    /**
     * Set transition properties when updating tab content.
     * - Dropdowns form has no missing manual component as it's not possible as
     *   the list of values is populated from metafield values on products.
     * @param {Boolean} missingManual - Opening missing manual component?
     */
    triggerAnimation(missingManual = true) {
      this.animationType = 'content'
      const defaultForm = this.$refs[this.activeTab?.id][0]?.$el
      const missingForm = this.$refs[`${this.activeTab?.id}Missing`][0]?.$el

      if (missingManual) {
        this.elements.activeTab = missingForm
        this.elements.previousTab = defaultForm

        this.updateFocus(this.activeTab?.id, missingForm)
        return
      }

      this.elements.activeTab = defaultForm
      this.elements.previousTab = missingForm

      this.updateFocus(this.activeTab?.id, defaultForm)
    },

    /**
     * Emit event for tracking.
     * @param {String} eventType - Event type.
     * @param {Object} parameters - Parameters for tracking.
     */
    emitEvent(eventType, parameters) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: eventType,
        ...parameters,
      })
    },
  },
}
</script>

<style lang="scss">
@import './vehicle-finder-form';
</style>
